import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useCurrentDoctor from "./useCurrentDoctor";
import { fetchPrescriptionsByPatientForDoctor } from "../../prescriptions/redux/thunks/prescriptionThunks";

export default function useFileRecentMedicalVisits(
  consultations = [],
  doctorIdProp = null
) {
  const dispatch = useDispatch();
  const { currentDoctor } = useCurrentDoctor();

  const [prescriptionsMap, setPrescriptionsMap] = useState({});
  const loadedConsultationsRef = useRef(new Set());
  const isLoadingRef = useRef(false);

  const loadPrescriptions = useCallback(async (onlyConsultationId = null) => {
    if (isLoadingRef.current) return;
    if (!consultations?.length) return;

    const doctorId =
      doctorIdProp ||
      currentDoctor?.id ||
      currentDoctor?.doctorId ||
      currentDoctor?._id;

    if (!doctorId) return;

    const consultationsToLoad = consultations.filter((consultation) => {
      const consultationId = consultation.id || consultation._id;
      return (
        consultationId &&
        (!onlyConsultationId ||
          String(consultationId) === String(onlyConsultationId))
      );
    });

    if (!consultationsToLoad.length) return;

    isLoadingRef.current = true;
    const newPrescriptions = {};

    try {
      const patientId = consultationsToLoad[0]?.userId;
      const result = await dispatch(
        fetchPrescriptionsByPatientForDoctor({
          doctorId,
          patientId,
        })
      );

      if (!result?.error) {
        const payload = result.payload;
        const documents = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.documents)
            ? payload.documents
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.data?.documents)
                ? payload.data.documents
                : [];

        consultationsToLoad.forEach((consultation) => {
          const consultationId = consultation.id || consultation._id;
          if (!consultationId) return;

          newPrescriptions[consultationId] = documents.filter(
            (document) =>
              String(document?.consultationId || "") === String(consultationId)
          );
          loadedConsultationsRef.current.add(consultationId);
        });
      }

      if (Object.keys(newPrescriptions).length > 0) {
        setPrescriptionsMap((previous) => ({
          ...previous,
          ...newPrescriptions,
        }));
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [consultations, currentDoctor, doctorIdProp, dispatch]);

  useEffect(() => {

    loadPrescriptions();

    const reloadDocuments = (event) => {
      loadPrescriptions(event?.detail?.consultationId || null);
    };

    window.addEventListener("prescriptions-updated", reloadDocuments);
    return () => {
      window.removeEventListener("prescriptions-updated", reloadDocuments);
    };
  }, [loadPrescriptions]);

  return {
    prescriptionsMap,
  };
}
